<template>
  <nav
    class="navbar navbar-expand"
    :class="{
      'fixed-bottom bg-primary-blue': isMobile,
      'd-none': isMobile && isFooterInViewport,
    }"
  >
    <div class="container-fluid justify-content-center">
      <ul class="navbar-nav row flex-row flex-md-column">
        <template v-if="!isMobile">
          <li class="px-md-4">
            <UserName />
          </li>
          <li class="px-md-4 nav-link d-none d-md-block">
            <div class="seperator my-1"></div>
          </li>
        </template>
        <li
          v-for="link in navLinks"
          :key="link.route"
          class="nav-item"
          :class="[link.class, { 'w-100': !isMobile }]"
        >
          <router-link :to="link.route" class="nav-link d-block" :class="{ 'w-100': !isMobile }">
            <div
              :class="`${navContentClasses} ${isActive(link.route) && activeNavClasses}`"
            >
              <component :is="link.icon" :class="iconClasses" />
              <span>{{ link.label }}</span>
            </div>
          </router-link>
        </li>
        <template v-if="!isMobile">
          <li class="px-md-4 nav-link">
            <div class="seperator my-1"></div>
          </li>
          <li class="px-md-4 nav-link">
            <Logout />
          </li>
        </template>
      </ul>
    </div>
  </nav>
</template>

<script lang="ts" setup>
  import {
    ref,
    onMounted,
    onBeforeUnmount,
    computed,
    ComputedRef,
    Ref,
  } from 'vue'

  // vueuse
  import { useElementVisibility } from '@vueuse/core'

  // Router
  import { useRoute } from 'vue-router'

  // Icons
  import ProfileIcon from '@/assets/ProfileIcon.vue'
  import ReservationIcon from '@/assets/ReservationIcon.vue'
  import BedIcon from '@/assets/BedIcon.vue'

  // Components
  import UserName from './UserName.vue'
  import Logout from './Logout.vue'

  // Config
  import { RouteConfig } from '@/config'

  // Composables
  import useScreenSize from '@/composables/useScreenSize'

  // Translations
  import { useI18n } from 'vue-i18n'
  const { t } = useI18n()

  const route = useRoute()

  // Define nav links for dynamic rendering
  const navLinks = ref([
    {
      icon: ReservationIcon,
      label: t('navbar.confirmed'),
      route: RouteConfig.CONFIRMED,
      class: 'col-3',
    },
    {
      icon: BedIcon,
      label: t('navbar.quotes'),
      route: RouteConfig.QUOTE,
      class: 'col-6 d-flex d-md-block justify-content-center',
    },
    {
      icon: ProfileIcon,
      label: t('navbar.profile'),
      route: RouteConfig.PROFILE,
      class: 'col-3',
    },
  ])

  // vueuse functionality
  const footerElem: Ref<HTMLElement | null> = ref(null)
  const isFooterInViewport: Ref<boolean> = useElementVisibility(footerElem)

  // Methods
  const isActive = (routeName: string) => {
    return route.path === `/account/${routeName}`
  }


  // Computed
  const { isMobile } = useScreenSize()

  const mobileTextClasses = 'subcaption text-primary-white'
  const desktopTextClasses = 'body text-grey-900'
  const navContentBaseClasses =
    'nav-content d-flex flex-column flex-lg-row align-items-center text-center py-md-2 px-md-3 border-radius-button-secondary'
  const iconClasses = 'mb-1 mb-lg-0 me-lg-3'
  const desktopActiveClasses = 'bg-primary-blue'

  const navContentClasses: ComputedRef<string> = computed(() => {
    return `${isMobile.value ? mobileTextClasses : desktopTextClasses} ${navContentBaseClasses}`
  })

  const activeNavClasses: ComputedRef<string> = computed(() => {
    return `${isMobile.value ? '' : desktopActiveClasses} active`
  })

  // Scroll listeners
  const setFooterElem = (): void => {
    footerElem.value = document.querySelector('.footer-register-container')
  }

  const setScrollListener = (): void => {
    // If the value has already been set, remove the listener
    if (footerElem.value) {
      removeScrollListener()
      return
    }
    window.addEventListener('scroll', setFooterElem, false)
  }

  const removeScrollListener = (): void => {
    window.removeEventListener('scroll', setFooterElem, false)
  }

  onMounted(() => {
    setFooterElem()
    setScrollListener()
  })

  onBeforeUnmount(() => {
    removeScrollListener()
  })
</script>

<style scoped>
  .nav-icon {
    margin-bottom: 5px; /* Space between icon and text */
  }

  .border-radius-button-secondary {
    border-radius: var(--border-radius-button-secondary);
  }

  .active {
    color: var(--primary-white);
    font-weight: 900;
  }

  .width-max-content {
    width: max-content;
  }

  @media (min-width: 768px) {
    .nav-item {
      width: 100%;
    }
    
    .nav-link {
      width: 100%;
      display: block;
    }
    
    .nav-content {
      width: 100%;
      min-width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    /* Ensure icon maintains its size */
    .nav-content svg {
      width: 25px !important;
      height: 25px !important;
      flex-shrink: 0;
    }
    
    .nav-content:hover {
      background-color: var(--blue-200) !important;
      color: var(--grey-900) !important;
    }

    .active {
      font-weight: 400;
    }
  }
</style>
